import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
} from "../../styles/componentStyles";
import { useFormik } from "formik";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import CourseCollegeValidationSchema from "../Validations/AcademicsValidation";

function CollegeCourseMapModal({ open, onClose, collegeMap, onSuccess }) {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);

  //  Form vlaidation using useFormik hook
  const formik = useFormik({
    initialValues: {
      universityId: collegeMap?.universityId ?? "",
      courseId: collegeMap?.id ?? "",
      active:
        collegeMap && typeof collegeMap.active !== "undefined"
          ? String(collegeMap.active)
          : "true",
    },

    validationSchema:
      CourseCollegeValidationSchema.CourseCollegeValidationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        setSubmitting(true);

        const payload = {
          universityId: values.universityId,
          courseIds: [values.courseId],
          active: values.active === "true",
        };

        const response = await api.post("/api/bulkCollegeCourses", payload);

        // Show success toast using the message returned by the API
        toast.success(
          response.data?.data?.message || "Course Mapped colleges successfully!",
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
        onSuccess(response.data.data);
        resetForm();
        onClose();
      } catch (error) {
        toast.error("Failed to submit. Please try again.", {
          autoClose: 3000,
        });
        console.error("API Error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Fetch dropdown data for universities
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const universitiesRes = await api.get("/getAllUniversities");
        setUniversities(universitiesRes.data.data);
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching dropdown data";
        setError(message);
        console.error("Dropdown fetch error:", err);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch all courses available for mapping
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesRes = await api.get("/getCourses", { params: { active: true } });
        setCourses(coursesRes.data.data);
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching dropdown data";
        setError(message);
        console.error("Dropdown fetch error:", err);
      }
    };

    fetchCourses();
  }, []);
  return (
    <>
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      <Modal open={open} onClose={onClose} disableScrollLock>
        <Box
          sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 500 } }}
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            Add College Course Mapping
          </Typography>
          <Box sx={FORM_STYLES.container}>
            <FormControl
              fullWidth
              sx={{ mt: 1 }}
              error={
                formik.touched.universityId &&
                Boolean(formik.errors.universityId)
              }
            >
              <InputLabel>University</InputLabel>
              <Select
                label="University"
                name="universityId"
                value={formik.values.universityId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
              >
                <MenuItem value="">None</MenuItem>
                {universities.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.universityId && formik.errors.universityId && (
                <FormHelperText>{formik.errors.universityId}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              fullWidth
              sx={{ mt: 2 }}
              error={formik.touched.courseId && Boolean(formik.errors.courseId)}
            >
              <InputLabel>Course</InputLabel>
              <Select
                label="Course"
                name="courseId"
                value={formik.values.courseId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
              >
                <MenuItem value="">None</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.courseName || c.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.courseId && formik.errors.courseId && (
                <FormHelperText>{formik.errors.courseId}</FormHelperText>
              )}
            </FormControl>
            <FormControl
              fullWidth
              error={formik.touched.active && Boolean(formik.errors.active)}
              sx={{ mt: 2 }}
            >
              <InputLabel id="active-label">Active</InputLabel>
              <Select
                labelId="active-label"
                id="active"
                name="active"
                value={formik.values.active}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Active"
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {formik.touched.active && formik.errors.active && (
                <FormHelperText>{formik.errors.active}</FormHelperText>
              )}
            </FormControl>

            <Box
              sx={FORM_STYLES.actions}
              style={{
                display: "flex",
                marginTop: "20px",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={formik.isSubmitting}
                {...BUTTON_STYLES.primary}
              >
                {formik.isSubmitting ? "Processing..." : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default CollegeCourseMapModal;
